import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Users, UserCheck, ClipboardList, Search, Camera, X, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL ?? ''}/api`;
const YELLOW = "#FFBD00";
const http = axios.create({ baseURL: API, withCredentials: true });

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-[#262626] bg-[#0C0C0C] p-6" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</span>
      <Icon size={16} style={{ color: YELLOW }} />
    </div>
    <div className="mt-3 font-display text-3xl font-semibold text-white">{value}</div>
  </div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#333] px-0 py-3 text-white outline-none transition-colors focus:border-[#FFBD00] placeholder:text-[#555]";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-[#262626] bg-[#0C0C0C] p-10" data-testid="admin-login-form">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: YELLOW }}>
          The Sessions Project
        </div>
        <h1 className="mt-2 font-display text-2xl font-semibold uppercase tracking-wider text-white">
          Admin Access
        </h1>
        <div className="mt-8 space-y-6">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputCls} data-testid="admin-email-input" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            className={inputCls} data-testid="admin-password-input" />
        </div>
        {error && (
          <p className="mt-5 text-sm text-red-400" data-testid="admin-login-error">{error}</p>
        )}
        <button type="submit" disabled={loading}
          className="mt-8 w-full bg-[#FFBD00] py-3 font-display text-sm font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-white disabled:opacity-50"
          data-testid="admin-login-button">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

// ── QR Scanner Component ──
const QrScanner = ({ onCodeScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState("Camera off");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  // Load jsQR dynamically
  const loadJsQr = () => {
    if (window.jsQR) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const startScanner = async () => {
    try {
      await loadJsQr();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 640 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setScanning(true);
      setStatus("Point camera at QR code");

      // Start decode loop
      intervalRef.current = setInterval(decodeFrame, 500);
    } catch (e) {
      setStatus("Camera access denied. Use manual code entry.");
      toast.error("Camera not available. Try manual entry.");
    }
  };

  const stopScanner = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
    setStatus("Camera off");
  };

  const decodeFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !window.jsQR) return;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert",
    });

    if (code && code.data) {
      // Extract check-in code from QR data URL: https://normanevent.com/data?checkin=XXXX
      const match = code.data.match(/checkin=([A-Za-z0-9]+)/i);
      const qrCode = match ? match[1].toUpperCase() : code.data.trim().toUpperCase();

      if (qrCode.length >= 4 && qrCode.length <= 20) {
        setStatus("Code detected! Validating…");
        stopScanner();
        onCodeScanned(qrCode);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="border border-[#262626] bg-[#0C0C0C] p-6">
      <h2 className="mb-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white">
        Scan QR Code
      </h2>
      <div className="relative mx-auto aspect-square max-w-[280px] overflow-hidden rounded-lg bg-black">
        <video ref={videoRef} className={`h-full w-full object-cover ${scanning ? "block" : "hidden"}`} playsInline muted />
        <canvas ref={canvasRef} className="hidden" />
        {!scanning && (
          <div className="flex h-full items-center justify-center text-[#555] text-sm">
            Camera off
          </div>
        )}
        {/* Scan frame corners */}
        {scanning && (
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-3 top-3 h-6 w-6" style={{ borderColor: YELLOW, borderStyle: "solid", borderWidth: "3px 0 0 3px" }} />
            <div className="absolute right-3 top-3 h-6 w-6" style={{ borderColor: YELLOW, borderStyle: "solid", borderWidth: "3px 3px 0 0" }} />
            <div className="absolute bottom-3 left-3 h-6 w-6" style={{ borderColor: YELLOW, borderStyle: "solid", borderWidth: "0 0 3px 3px" }} />
            <div className="absolute bottom-3 right-3 h-6 w-6" style={{ borderColor: YELLOW, borderStyle: "solid", borderWidth: "0 3px 3px 0" }} />
          </div>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-[#A3A3A3]">{status}</p>
      <div className="mt-3 text-center">
        {scanning ? (
          <button onClick={stopScanner}
            className="inline-flex items-center gap-2 border border-red-500 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-red-400 transition-colors hover:bg-red-500/10">
            <X size={14} /> Stop Camera
          </button>
        ) : (
          <button onClick={startScanner}
            className="inline-flex items-center gap-2 border border-[#333] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-[#FFBD00] hover:text-[#FFBD00]">
            <Camera size={14} /> Start Camera
          </button>
        )}
      </div>
    </div>
  );
};

// ── Manual Code Entry ──
const ManualEntry = ({ onCodeSubmit }) => {
  const [code, setCode] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!code || code.length < 4) {
      toast.error("Please enter a valid code");
      return;
    }
    onCodeSubmit(code.toUpperCase());
    setCode("");
  };

  return (
    <div className="border border-[#262626] bg-[#0C0C0C] p-6">
      <h2 className="mb-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white">
        Manual Code Entry
      </h2>
      <p className="mb-4 text-xs text-[#A3A3A3]">Type or paste the check-in code from the guest's ticket</p>
      <form onSubmit={submit} className="flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. 4F3A2C8B"
          maxLength={8}
          className="flex-1 border-b border-[#333] bg-transparent px-0 py-3 text-center font-mono text-lg tracking-[0.3em] text-white outline-none transition-colors focus:border-[#FFBD00] placeholder:text-[#555]"
        />
        <button type="submit"
          className="border border-[#333] px-5 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-[#FFBD00] hover:text-[#FFBD00]">
          Validate
        </button>
      </form>
    </div>
  );
};

// ── Check-in Result Display ──
const CheckinResult = ({ result, onUndo, onContinue }) => {
  if (!result) return null;
  const r = result.rsvp;
  return (
    <div className={`border p-6 ${result.found ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"}`}>
      {result.found ? (
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-400" />
              <span className="font-display text-lg font-semibold text-white">{r.name}</span>
            </div>
            <p className="mt-1 text-xs text-[#A3A3A3]">{r.email}</p>
            <p className="mt-2 text-xs text-[#A3A3A3]">{r.guests} guest(s) · Code: {r.checkin_code}</p>
            <p className="text-xs text-green-400">
              {result.checked_in ? `✓ Checked in${r.checked_in_at ? " at " + new Date(r.checked_in_at).toLocaleTimeString() : ""}` : "Pending"}
            </p>
          </div>
          <div className="flex gap-2">
            {onUndo && (
              <button onClick={() => onUndo(r.checkin_code)}
                className="border border-[#333] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] transition-colors hover:border-yellow-500 hover:text-yellow-400">
                Undo
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <XCircle size={20} className="text-red-400" />
          <div>
            <span className="font-semibold text-red-400">Invalid Code</span>
            <p className="mt-1 text-xs text-[#A3A3A3]">No RSVP found for this code</p>
          </div>
        </div>
      )}
      <div className="mt-3">
        <button onClick={onContinue}
          className="border border-[#333] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-[#FFBD00] hover:text-[#FFBD00]">
          Scan Next
        </button>
      </div>
    </div>
  );
};

// ── Main Admin Component ──
export default function Admin() {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [highlightCode, setHighlightCode] = useState(null);
  const [checkinResult, setCheckinResult] = useState(null);

  const load = useCallback(async () => {
    const { data } = await http.get("/admin/rsvps");
    setData(data);
  }, []);

  useEffect(() => {
    http.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // QR check-in via ?checkin=CODE
  useEffect(() => {
    if (!user) return;
    const code = new URLSearchParams(window.location.search).get("checkin");
    if (!code) return;
    window.history.replaceState({}, "", "/data");
    http
      .post(`/admin/checkin/${code}`, { checked_in: true })
      .then(({ data }) => {
        setHighlightCode(code.toUpperCase());
        toast.success(`Checked in: ${data.name} (${data.guests} guests)`);
        load();
      })
      .catch(() => toast.error("Invalid check-in code"));
  }, [user, load]);

  const toggleCheckin = async (rsvp) => {
    try {
      const { data } = await http.post(`/admin/checkin/${rsvp.checkin_code}`, { checked_in: !rsvp.checked_in });
      toast.success(rsvp.checked_in ? `Undo check-in: ${rsvp.name}` : `Checked in: ${rsvp.name}`);
      setCheckinResult({ found: true, rsvp: data, checked_in: !rsvp.checked_in });
      load();
    } catch {
      toast.error("Check-in failed");
    }
  };

  // Handle QR scan or manual code submission
  const handleCode = async (code) => {
    try {
      const { data } = await http.post(`/admin/checkin/${code}`, { checked_in: true });
      setHighlightCode(code);
      setCheckinResult({ found: true, rsvp: data, checked_in: true });
      toast.success(`Checked in: ${data.name} (${data.guests} guests)`);
      load();
    } catch (err) {
      if (err.response?.status === 404) {
        setCheckinResult({ found: false, rsvp: null, checked_in: false });
        toast.error("Invalid check-in code");
      } else {
        toast.error("Check-in failed");
      }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await http.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sessions-project-rsvps.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
    setData(null);
  };

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-[#050505] text-[#555]">Loading…</div>;
  }
  if (user === false) {
    return <Login onLogin={setUser} />;
  }

  const rsvps = (data?.rsvps || []).filter(
    (r) =>
      !query ||
      r.name.toLowerCase().includes(query.toLowerCase()) ||
      r.email.toLowerCase().includes(query.toLowerCase()) ||
      r.checkin_code.toLowerCase().includes(query.toLowerCase())
  );
  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-[#1A1A1A] px-6 py-4 md:px-10">
        <div>
          <span className="font-display text-sm font-semibold uppercase tracking-[0.3em]">The Sessions Project</span>
          <span className="ml-3 text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: YELLOW }}>
            Guest List
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportCsv}
            className="flex items-center gap-2 border border-[#333] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-[#FFBD00] hover:text-[#FFBD00]"
            data-testid="admin-export-csv-button">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={logout}
            className="flex items-center gap-2 border border-[#333] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] transition-colors hover:border-red-500 hover:text-red-400"
            data-testid="admin-logout-button">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <div className="grid grid-cols-2 gap-px lg:grid-cols-4">
          <StatCard icon={ClipboardList} label="Total RSVPs" value={stats.total_rsvps ?? 0} testId="stat-total-rsvps" />
          <StatCard icon={Users} label="Total Guests" value={stats.total_guests ?? 0} testId="stat-total-guests" />
          <StatCard icon={UserCheck} label="Checked-in RSVPs" value={stats.checked_in ?? 0} testId="stat-checked-in" />
          <StatCard icon={UserCheck} label="Checked-in Guests" value={stats.checked_in_guests ?? 0} testId="stat-checked-in-guests" />
        </div>

        {/* ── QR Scanner + Manual Entry Section ── */}
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <QrScanner onCodeScanned={handleCode} />
          <div className="space-y-4">
            <ManualEntry onCodeSubmit={handleCode} />
            {checkinResult && (
              <CheckinResult
                result={checkinResult}
                onUndo={(code) => toggleCheckin({ checkin_code: code, checked_in: true })}
                onContinue={() => setCheckinResult(null)}
              />
            )}
          </div>
        </div>

        {/* ── Guest Table ── */}
        <div className="mt-8 flex items-center gap-3 border-b border-[#262626] pb-3">
          <Search size={16} className="text-[#555]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email or check-in code…"
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#555]"
            data-testid="admin-search-input"
          />
        </div>

        <div className="mt-6 overflow-x-auto border border-[#262626]">
          <table className="w-full text-left text-sm" data-testid="admin-rsvp-table">
            <thead>
              <tr className="border-b border-[#262626] bg-[#0C0C0C] text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Guests</th>
                <th className="px-5 py-4">Notes</th>
                <th className="px-5 py-4">Code</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4" />
              </tr>
            </thead>
            <tbody>
              {rsvps.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-[#555]" data-testid="admin-empty-state">
                    No RSVPs yet.
                  </td>
                </tr>
              )}
              {rsvps.map((r) => (
                <tr key={r.id}
                  className={`border-b border-[#1A1A1A] transition-colors hover:bg-[#0C0C0C] ${
                    highlightCode === r.checkin_code ? "bg-[#FFBD00]/10" : ""
                  }`}
                  data-testid={`rsvp-row-${r.checkin_code}`}>
                  <td className="px-5 py-4 font-medium text-white">{r.name}</td>
                  <td className="px-5 py-4 text-[#A3A3A3]">{r.email}</td>
                  <td className="px-5 py-4 text-white">{r.guests}</td>
                  <td className="max-w-[220px] truncate px-5 py-4 text-[#A3A3A3]" title={r.notes}>{r.notes || "—"}</td>
                  <td className="px-5 py-4 font-mono text-xs tracking-widest" style={{ color: YELLOW }}>{r.checkin_code}</td>
                  <td className="px-5 py-4">
                    {r.checked_in ? (
                      <span className="bg-[#FFBD00] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-black">Checked In</span>
                    ) : (
                      <span className="border border-[#333] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Pending</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => toggleCheckin(r)}
                      className="border border-[#333] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors hover:border-[#FFBD00] hover:text-[#FFBD00]"
                      data-testid={`checkin-toggle-${r.checkin_code}`}>
                      {r.checked_in ? "Undo" : "Check In"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
